
import React, { useMemo } from 'react';
import { ShieldAlert, ArrowLeft, LayoutDashboard, MessageSquare, Wrench, Wallet, Building, FileText } from 'lucide-react';
import { useNavigate } from '../../routes';
import { useAppStore } from '../../store';
import { UserRole } from '@repo/shared-types';
import { ROUTES } from '@repo/shared-types/routeRegistry';

// Role-based suggested routes
const ROLE_SUGGESTIONS: Record<UserRole, Array<{ label: string; route: string; icon: React.ElementType }>> = {
  [UserRole.PROPERTY_MANAGER]: [
    { label: 'Dashboard', route: '/app', icon: LayoutDashboard },
    { label: 'Properties', route: '/app/properties', icon: Building },
    { label: 'Maintenance', route: '/app/maintenance', icon: Wrench },
    { label: 'Communications', route: '/app/communications', icon: MessageSquare },
  ],
  [UserRole.TENANT]: [
    { label: 'My Home', route: ROUTES.MY_HOME, icon: LayoutDashboard },
    { label: 'Payments', route: '/app/my-payments', icon: Wallet },
    { label: 'Maintenance Requests', route: '/app/my-maintenance', icon: Wrench },
    { label: 'Messages', route: '/app/communications', icon: MessageSquare },
  ],
  [UserRole.OWNER]: [
    { label: 'Dashboard', route: '/app/owner/dashboard', icon: LayoutDashboard },
    { label: 'My Properties', route: '/app/owner/properties', icon: Building },
    { label: 'Maintenance', route: '/app/maintenance', icon: Wrench },
    { label: 'Financials', route: '/app/owner/statements', icon: Wallet },
  ],
  [UserRole.INVESTOR]: [
    { label: 'Portfolio Overview', route: '/app', icon: LayoutDashboard },
    { label: 'Investment Programs', route: '/app/investments', icon: Building },
    { label: 'Distributions', route: '/app/investments/distributions', icon: Wallet },
    { label: 'Documents', route: ROUTES.INVESTOR_DOCUMENTS, icon: FileText },
  ],
  [UserRole.VENDOR]: [
    { label: 'Dashboard', route: '/app', icon: LayoutDashboard },
    { label: 'My Jobs', route: '/app/vendor/jobs', icon: Wrench },
    { label: 'Financials', route: '/app/vendor/financials', icon: Wallet },
    { label: 'My Profile', route: '/app/vendor/profile', icon: Building },
  ],
  [UserRole.ADMIN]: [
    { label: 'Dashboard', route: '/app', icon: LayoutDashboard },
    { label: 'System Settings', route: '/app/settings', icon: LayoutDashboard },
    { label: 'Communications', route: '/app/communications', icon: MessageSquare },
  ],
  [UserRole.SYSTEM_ADMIN]: [
    { label: 'Dashboard', route: '/app', icon: LayoutDashboard },
    { label: 'System Settings', route: '/app/settings', icon: LayoutDashboard },
    { label: 'Communications', route: '/app/communications', icon: MessageSquare },
  ],
  [UserRole.PLATFORM_ADMIN]: [
    { label: 'Dashboard', route: '/app', icon: LayoutDashboard },
    { label: 'System Settings', route: '/app/settings', icon: LayoutDashboard },
    { label: 'Communications', route: '/app/communications', icon: MessageSquare },
  ],
};

export const UnauthorizedView: React.FC = () => {
  const navigate = useNavigate();
  const { userRole } = useAppStore();

  const suggestions = useMemo(() => {
    return ROLE_SUGGESTIONS[userRole] || ROLE_SUGGESTIONS[UserRole.PROPERTY_MANAGER];
  }, [userRole]);

  return (
    <div className="flex flex-col items-center justify-center h-full bg-slate-50 dark:bg-slate-950 p-6 animate-fade-in">
      <div className="bg-white dark:bg-slate-900 p-8 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-800 text-center max-w-md w-full">
        <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-full flex items-center justify-center mx-auto mb-6">
          <ShieldAlert size={32} />
        </div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Access Denied</h1>
        <p className="text-slate-500 dark:text-slate-400 mb-6">
          You do not have permission to view this page. If you believe this is an error, please contact your administrator.
        </p>

        {suggestions.length > 0 && (
          <div className="mb-6 text-left">
            <p className="text-sm font-bold text-slate-700 dark:text-slate-300 mb-3">You might be looking for:</p>
            <div className="space-y-2">
              {suggestions.map((suggestion) => {
                const Icon = suggestion.icon;
                return (
                  <button
                    key={suggestion.route}
                    onClick={() => navigate(suggestion.route)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition text-left"
                  >
                    <Icon size={18} className="text-slate-500 dark:text-slate-400" />
                    <span>{suggestion.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <button 
          onClick={() => navigate('/app')}
          className="w-full flex items-center justify-center bg-slate-900 dark:bg-white text-white dark:text-slate-900 py-3 rounded-xl font-bold transition hover:opacity-90"
        >
          <ArrowLeft size={18} className="mr-2" /> Return Home
        </button>
      </div>
    </div>
  );
};


